/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

(() => {
  let overlayApi = null;
  let currentList = [];
  let currentIndex = 0;

  const syncNav = () => {
    if (!overlayApi) return;
    if (overlayApi.prevBtn) overlayApi.prevBtn.disabled = currentIndex <= 0;
    if (overlayApi.nextBtn) overlayApi.nextBtn.disabled = currentIndex >= currentList.length - 1;
    if (overlayApi.counter) overlayApi.counter.textContent = `${Math.min(currentIndex + 1, currentList.length)}/${currentList.length}`;
  };

  const openAt = (src) => {
    if (!overlayApi || !overlayApi.overlay.isConnected) {
      overlayApi = buildOverlay();
    }
    if (!src) return;
    overlayApi.img.src = src;
    overlayApi.overlay.setAttribute('open', '');
    document.body.style.overflow = 'hidden';
    syncNav();
  };

  const goto = (dir) => {
    const next = currentIndex + dir;
    if (next < 0 || next >= currentList.length) return;
    currentIndex = next;
    openAt(currentList[currentIndex]);
  };

  const buildOverlay = () => {
    const overlay = document.createElement('div');
    overlay.className = 'qb-image-zoom-overlay';
    overlay.innerHTML = `
      <div class="qb-image-zoom-dialog" role="dialog" aria-modal="true" aria-label="画像拡大">
        <div class="qb-image-zoom-topbar">
          <button type="button" class="qb-image-zoom-close" aria-label="閉じる">
            <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
              <rect x="0.5" y="0.5" width="39" height="39" rx="2.5" stroke="black"/>
              <line x1="13.3536" y1="13.1464" x2="26.3536" y2="26.1464" stroke="black"/>
              <line x1="12.6464" y1="26.1464" x2="25.6464" y2="13.1464" stroke="black"/>
            </svg>
          </button>
        </div>

        <div class="qb-image-zoom-stage">
          <img alt="" />
        </div>

        <div class="qb-image-zoom-bottombar" aria-label="画像ナビゲーション">
          <button type="button" class="qb-image-zoom-nav qb-image-zoom-nav--prev" aria-label="前へ">
            <svg width="17" height="15" viewBox="0 0 17 15" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
              <line x1="2.68359" y1="6.83984" x2="16.6836" y2="6.83984" stroke="black"/>
              <path d="M7.18359 14.3398L0.683594 7.33984L7.18359 0.339844" stroke="black"/>
            </svg>
          </button>
          <div class="qb-image-zoom-counter" aria-live="polite">1/1</div>
          <button type="button" class="qb-image-zoom-nav qb-image-zoom-nav--next" aria-label="次へ">
            <svg width="17" height="15" viewBox="0 0 17 15" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
              <line x1="2.68359" y1="6.83984" x2="16.6836" y2="6.83984" stroke="black"/>
              <path d="M7.18359 14.3398L0.683594 7.33984L7.18359 0.339844" stroke="black"/>
            </svg>
          </button>
        </div>
      </div>
    `;
    document.body.appendChild(overlay);

    const img = overlay.querySelector('.qb-image-zoom-stage img');
    const closeBtn = overlay.querySelector('.qb-image-zoom-close');
    const prevBtn = overlay.querySelector('.qb-image-zoom-nav--prev');
    const nextBtn = overlay.querySelector('.qb-image-zoom-nav--next');
    const counter = overlay.querySelector('.qb-image-zoom-counter');

    const close = () => {
      overlay.removeAttribute('open');
      document.body.style.overflow = '';
      if (img) img.src = '';
    };

    // ドロワー側へ伝播して閉じないように止める（capture は使わない）
    const stopBubble = (e) => e.stopPropagation();

    overlay.addEventListener('pointerdown', stopBubble);

    overlay.addEventListener('click', (e) => {
      e.stopPropagation();
      if (e.target === overlay) close();
    });

    overlay.querySelector('.qb-image-zoom-dialog')?.addEventListener('pointerdown', stopBubble);
    overlay.querySelector('.qb-image-zoom-dialog')?.addEventListener('click', stopBubble);

    // ×だけは確実にドロワーへ伝播させない
    closeBtn?.addEventListener('pointerdown', (e) => {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
    }, true);

    closeBtn?.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      close();
    }, true);

    prevBtn?.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      goto(-1);
    });

    nextBtn?.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      goto(1);
    });

    document.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape' && e.key !== 'Esc' && e.keyCode !== 27) return;
      if (!overlay.hasAttribute('open')) return;
      e.preventDefault();
      e.stopPropagation();
      close();
    }, true);

    return { overlay, img, close, prevBtn, nextBtn, counter };
  };

  document.addEventListener('click', (e) => {
    const btn = e.target?.closest?.('.qb-matrix__image-zoom');
    if (!btn) return;

    const openDrawer = document.querySelector('.drawer.drawer--quick-buy[open]') || btn.closest('.drawer.drawer--quick-buy');
    const scope = openDrawer || document;
    const buttons = [...scope.querySelectorAll('.qb-matrix__image-zoom[data-qb-zoom-src]')];
    currentList = buttons.map((b) => b.getAttribute('data-qb-zoom-src')).filter(Boolean);
    const src = btn.getAttribute('data-qb-zoom-src');
    if (!src || currentList.length === 0) return;
    currentIndex = Math.max(0, currentList.indexOf(src));

    e.preventDefault();
    e.stopPropagation();

    openAt(currentList[currentIndex]);
  });
})();

(() => {
  const PDP_SECTION_SELECTOR = '.shopify-section--main-product';
  const PDP_WISHLIST_SLOT_SELECTOR = '[data-pdp-wishlist-slot]';
  const PDP_WISHLIST_BUTTON_SELECTOR = [
    '.product-meta__wishlist-slot :is(button.wk-button, a.wk-button)',
    '.product-form :is(button.wk-button, a.wk-button)',
    '.drawer:not([open]) .qb-matrix__swish-slot :is(button.wk-button, a.wk-button, wishlist-button-block, wk-button)',
  ].join(', ');

  const isPdpWishlistControlTarget = (node) =>
    node && !node.closest('.product-meta__productions');

  let pdpWishlistObserver = null;

  const isPdpWishlistAppBlock = (node, slot) => {
    if (!node || slot.contains(node)) return false;
    if (node.id && node.id.includes('wishlist')) return true;
    if (node.matches?.('wishlist-button-block, wk-button')) return true;
    return Boolean(node.querySelector('wishlist-button-block, wk-button'));
  };

  const findPdpWishlistAppBlock = (slot, formArea) => {
    if (!formArea) return null;

    const buy = formArea.querySelector('.product-form__buy-buttons');
    const buyApp = buy?.querySelector('shopify-app-block, .shopify-app-block');
    if (buyApp && isPdpWishlistAppBlock(buyApp, slot)) return buyApp;

    const apps = formArea.querySelectorAll('shopify-app-block, .shopify-app-block');
    for (const app of apps) {
      if (isPdpWishlistAppBlock(app, slot)) return app;
    }

    return null;
  };

  const relocatePdpWishlist = (section) => {
    const slot = section.querySelector(PDP_WISHLIST_SLOT_SELECTOR);
    if (!slot) return false;

    const fallback = slot.querySelector('[data-pdp-wishlist-fallback]');
    const formArea = section.querySelector('.product-form');
    const app = findPdpWishlistAppBlock(slot, formArea);
    if (!app || slot.contains(app)) return false;

    slot.insertBefore(app, fallback);
    if (fallback) fallback.hidden = true;
    return true;
  };

  const isPdpStickyBarVisible = (section) => {
    const sticky = section.querySelector('.product-sticky-form');
    return Boolean(sticky && !sticky.hasAttribute('hidden'));
  };

  const syncPdpWishlistButton = () => {
    const section = document.querySelector(PDP_SECTION_SELECTOR);
    if (!section) return;

    relocatePdpWishlist(section);

    const slot = section.querySelector(PDP_WISHLIST_SLOT_SELECTOR);
    const stickyVisible = isPdpStickyBarVisible(section);
    section.classList.toggle('is-pdp-sticky-bar-visible', stickyVisible);

    if (slot) {
      slot.toggleAttribute('hidden', stickyVisible);
    }

    section.querySelectorAll(PDP_WISHLIST_BUTTON_SELECTOR).forEach((node) => {
      if (!isPdpWishlistControlTarget(node)) return;

      if (stickyVisible) {
        node.style.setProperty('display', 'none', 'important');
        node.style.pointerEvents = 'none';
        return;
      }

      node.style.removeProperty('display');
      node.style.pointerEvents = '';
      node.style.top = '';
      node.style.right = '';
      node.style.position = '';
    });
  };

  const watchPdpWishlist = () => {
    const section = document.querySelector(PDP_SECTION_SELECTOR);
    if (!section) return;

    if (pdpWishlistObserver) {
      pdpWishlistObserver.disconnect();
    }

    pdpWishlistObserver = new MutationObserver(syncPdpWishlistButton);
    pdpWishlistObserver.observe(section, {
      attributes: true,
      attributeFilter: ['hidden'],
      childList: true,
      subtree: true,
    });

    syncPdpWishlistButton();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', watchPdpWishlist);
  } else {
    watchPdpWishlist();
  }

  document.addEventListener('shopify:section:load', (event) => {
    if (event.target?.classList?.contains('shopify-section--main-product')) {
      watchPdpWishlist();
    }
  });

  window.addEventListener('resize', syncPdpWishlistButton);
  window.addEventListener('scroll', syncPdpWishlistButton, { passive: true });
})();

(() => {
  const MOBILE_MQ = '(max-width: 740px)';
  let placeholder = null;

  const getStaffStylingBlock = () => {
    const mainProduct = document.querySelector('.shopify-section--main-product');

    return (
      mainProduct?.querySelector('.staff-styling')
      || mainProduct?.querySelector('.product__media-custom-liquid:has([id^="leeep-tag-"])')
      || document.querySelector('body.template-product .staff-styling')
    );
  };

  const relocateStaffStyling = () => {
    const staff = getStaffStylingBlock();
    if (!staff) return;

    const isMobile = window.matchMedia(MOBILE_MQ).matches;

    if (!isMobile) {
      if (placeholder && placeholder.parentNode) {
        placeholder.parentNode.insertBefore(staff, placeholder.nextSibling);
      }
      return;
    }

    if (!placeholder && staff.parentNode) {
      placeholder = document.createComment('staff-styling-placeholder');
      staff.parentNode.insertBefore(placeholder, staff);
    }

    const recommendations = document.querySelector('.shopify-section--product-recommendations');
    if (!recommendations) return;

    if (staff.nextElementSibling !== recommendations) {
      recommendations.insertAdjacentElement('beforebegin', staff);
    }
  };

  const start = () => {
    const startedAt = Date.now();
    const tick = () => {
      relocateStaffStyling();
      if (Date.now() - startedAt > 8000) return;
      window.setTimeout(tick, 200);
    };
    tick();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  window.addEventListener('resize', relocateStaffStyling);

  document.addEventListener('shopify:section:load', (event) => {
    if (
      event.target?.classList?.contains('shopify-section--main-product')
      || event.target?.classList?.contains('shopify-section--product-recommendations')
    ) {
      relocateStaffStyling();
    }
  });
})();

(() => {
  const showDrawerCartNotification = (root) => {
    const notification = root?.querySelector?.('cart-notification.cart-notification--drawer') || root?.querySelector?.('cart-notification');
    if (!notification) return;

    notification.dispatchEvent(new CustomEvent('cart-notification:show', {
      bubbles: true,
      cancelable: true,
      detail: { status: 'success', error: '' }
    }));
  };

  document.addEventListener('variant:added', (event) => {
    const target = event.target;
    const quickBuyDrawer = target?.closest?.('.drawer.drawer--quick-buy') || target?.closest?.('quick-buy-drawer')?.closest?.('.drawer.drawer--quick-buy');
    if (!quickBuyDrawer) return;

    showDrawerCartNotification(quickBuyDrawer);
  });
})();

(() => {
  const isQuickBuyDrawerForm = (form) => {
    if (!form) return false;
    const drawer = form.closest?.('.drawer.drawer--quick-buy');
    if (!drawer) return false;
    return drawer.hasAttribute('open');
  };

  const showDrawerCartNotification = (drawer, status, error) => {
    const notification =
      drawer?.querySelector?.('cart-notification.cart-notification--drawer') ||
      drawer?.querySelector?.('cart-notification');
    if (!notification) return;

    notification.dispatchEvent(new CustomEvent('cart-notification:show', {
      bubbles: true,
      cancelable: true,
      detail: { status, error: error || '' }
    }));
  };

  const syncCartEvents = async (variantPayload, openMiniCart) => {
    const cartBase = window.themeVariables?.routes?.cartUrl || '/cart';
    const cartUrl = cartBase.endsWith('.js') ? cartBase : `${cartBase}.js`;
    const cartRes = await fetch(cartUrl);
    const cartContent = await cartRes.json();

    document.documentElement.dispatchEvent(new CustomEvent('cart:updated', {
      bubbles: true,
      detail: { cart: cartContent }
    }));

    cartContent.sections = variantPayload?.sections;
    document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
      bubbles: true,
      detail: { cart: cartContent, openMiniCart: !!openMiniCart }
    }));
  };

  document.addEventListener('submit', async (e) => {
    const form = e.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (!isQuickBuyDrawerForm(form)) return;

    const drawer = form.closest('.drawer.drawer--quick-buy');

    // テーマの ProductForm submit（リダイレクト）より先に奪う
    e.preventDefault();
    e.stopPropagation();
    e.stopImmediatePropagation();

    try {
      const submitButtons = Array.from(form.elements).filter((el) => el?.tagName === 'BUTTON' && el.type === 'submit');
      submitButtons.forEach((btn) => {
        btn.setAttribute('disabled', 'disabled');
        btn.setAttribute('aria-busy', 'true');
      });

      const productForm = new FormData(form);
      productForm.append('sections', ['mini-cart']);
      productForm.delete('option1');
      productForm.delete('option2');
      productForm.delete('option3');

      const cartAddBase = window.themeVariables?.routes?.cartAddUrl || '/cart/add';
      const cartAddUrl = cartAddBase.endsWith('.js') ? cartAddBase : `${cartAddBase}.js`;

      const res = await fetch(cartAddUrl, {
        method: 'POST',
        body: productForm,
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      });

      let json = null;
      const ct = res.headers.get('content-type') || '';
      if (ct.includes('application/json')) {
        json = await res.json();
      } else {
        const text = await res.text();
        json = { description: text };
      }

      submitButtons.forEach((btn) => {
        btn.removeAttribute('disabled');
        btn.removeAttribute('aria-busy');
      });

      if (!res.ok) {
        showDrawerCartNotification(drawer, 'error', json?.description || '');
        return;
      }

      showDrawerCartNotification(drawer, 'success', '');

      // カート内容・ミニカート更新（リダイレクトせず同期）
      await syncCartEvents(json, false);
    } catch (err) {
      const msg = err?.message || 'エラーが発生した';
      const drawer = form.closest?.('.drawer.drawer--quick-buy');
      showDrawerCartNotification(drawer, 'error', msg);
    }
  }, true);

  const RES_PROP_DATE = 'properties[発送予定日]';

  const upsertReservationHiddenInput = (form, name, value) => {
    const existing = form.querySelector(`input[name="${name}"]`);
    if (!value) {
      existing?.remove();
      return;
    }
    const input = existing || document.createElement('input');
    input.type = 'hidden';
    input.name = name;
    input.value = value;
    if (!existing) {
      const anchor = form.querySelector('[data-can-reservation-fields]') || form.querySelector('[name="id"]');
      if (anchor?.parentNode) {
        anchor.parentNode.insertBefore(input, anchor.nextSibling);
      } else {
        form.appendChild(input);
      }
    }
  };

  const syncReservationLineProperties = (form, variantId) => {
    const mapScript = form.closest('.product-form')?.querySelector('[data-can-reservation-map]');
    if (!mapScript) return;

    let map;
    try {
      map = JSON.parse(mapScript.textContent);
    } catch {
      return;
    }

    const data = map[String(variantId)];
    if (!data?.is_reservation) {
      upsertReservationHiddenInput(form, RES_PROP_DATE, '');
      return;
    }

    upsertReservationHiddenInput(form, RES_PROP_DATE, data.reservation_date || '');
  };

  document.addEventListener('variant:changed', (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    const variantId = event.detail?.variant?.id;
    if (!variantId) return;
    syncReservationLineProperties(form, variantId);
  });
})();